// Auth store for managing authentication state
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { authService } from '@/services/auth'
import type { TokenResponse, CognitoUser } from '@/config/cognito'
import { captureSentryException, addSentryBreadcrumb } from '@/config/sentry'

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const user = ref<CognitoUser | null>(null)
  const tokens = ref<TokenResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isLoggedIn = computed(() => isAuthenticated.value && user.value !== null)
  const accessToken = computed(() => tokens.value?.access_token || null)
  const idToken = computed(() => tokens.value?.id_token || null)
  const refreshToken = computed(() => tokens.value?.refresh_token || null)
  const userEmail = computed(() => user.value?.email || null)
  const userName = computed(() => user.value?.name || user.value?.given_name || null)

  // Actions
  const setLoading = (state: boolean) => {
    loading.value = state
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const setAuthenticated = (authTokens: TokenResponse, userData: CognitoUser) => {
    try {
      isAuthenticated.value = true
      tokens.value = authTokens
      user.value = userData
      error.value = null

      // Persist tokens to localStorage
      localStorage.setItem('auth_tokens', JSON.stringify(authTokens))
      localStorage.setItem('user_info', JSON.stringify(userData))

      addSentryBreadcrumb(`User authenticated: ${userData.email}`, 'auth')
    } catch (err) {
      captureSentryException(err as Error, {
        store: 'AuthStore',
        action: 'setAuthenticated'
      })
      throw err
    }
  }

  const clearAuthentication = () => {
    try {
      isAuthenticated.value = false
      tokens.value = null
      user.value = null
      error.value = null

      // Clear persisted data
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('user_info')

      addSentryBreadcrumb('User authentication cleared', 'auth')
    } catch (err) {
      captureSentryException(err as Error, {
        store: 'AuthStore',
        action: 'clearAuthentication'
      })
    }
  }

  const login = async (registry: string = 'sor') => {
    try {
      setLoading(true)
      setError(null)
      
      addSentryBreadcrumb(`Initiating login for registry: ${registry}`, 'auth')
      authService.redirectToLogin(registry)
    } catch (err) {
      const error = err as Error
      setError(error.message)
      captureSentryException(error, {
        store: 'AuthStore',
        action: 'login',
        registry
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const handleAuthCallback = async (code: string, state: string) => {
    try {
      setLoading(true)
      setError(null)

      addSentryBreadcrumb('Processing auth callback', 'auth')

      // Exchange code for tokens
      const authTokens = await authService.exchangeCodeForTokens(code, state)
      
      // Get user information
      const userData = await authService.getUserInfo(authTokens.access_token)
      
      // Set authenticated state
      setAuthenticated(authTokens, userData)

      addSentryBreadcrumb('Auth callback processed successfully', 'auth')
      return { success: true }
    } catch (err) {
      const error = err as Error
      setError(error.message)
      clearAuthentication()
      
      captureSentryException(error, {
        store: 'AuthStore',
        action: 'handleAuthCallback'
      })
      
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      setLoading(true)
      addSentryBreadcrumb('User logging out', 'auth')
      
      // Clear local state first
      clearAuthentication()
      
      // Redirect to Cognito logout
      authService.logout()
    } catch (err) {
      const error = err as Error
      setError(error.message)
      captureSentryException(error, {
        store: 'AuthStore',
        action: 'logout'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const refreshTokens = async () => {
    try {
      if (!tokens.value?.refresh_token) {
        throw new Error('No refresh token available')
      }

      setLoading(true)
      addSentryBreadcrumb('Refreshing tokens', 'auth')

      const newTokens = await authService.refreshTokens(tokens.value.refresh_token)
      
      // Update tokens while preserving user data
      tokens.value = newTokens
      localStorage.setItem('auth_tokens', JSON.stringify(newTokens))

      addSentryBreadcrumb('Tokens refreshed successfully', 'auth')
      return { success: true }
    } catch (err) {
      const error = err as Error
      setError(error.message)
      
      // If refresh fails, clear auth state
      clearAuthentication()
      
      captureSentryException(error, {
        store: 'AuthStore',
        action: 'refreshTokens'
      })
      
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const checkAuthState = async () => {
    try {
      setLoading(true)
      addSentryBreadcrumb('Checking auth state', 'auth')

      const authState = await authService.validateAuthState()
      
      if (authState.isAuthenticated && authState.user) {
        // Get current tokens from localStorage
        const storedTokens = localStorage.getItem('auth_tokens')
        if (storedTokens) {
          tokens.value = JSON.parse(storedTokens)
          user.value = authState.user
          isAuthenticated.value = true
          
          addSentryBreadcrumb(`Auth state restored for: ${authState.user.email}`, 'auth')
        }
      } else {
        clearAuthentication()
      }

      return authState.isAuthenticated
    } catch (err) {
      const error = err as Error
      setError(error.message)
      clearAuthentication()
      
      captureSentryException(error, {
        store: 'AuthStore',
        action: 'checkAuthState'
      })
      
      return false
    } finally {
      setLoading(false)
    }
  }

  const updateUserInfo = async () => {
    try {
      if (!tokens.value?.access_token) {
        throw new Error('No access token available')
      }

      setLoading(true)
      addSentryBreadcrumb('Updating user info', 'auth')

      const userData = await authService.getUserInfo(tokens.value.access_token)
      user.value = userData
      localStorage.setItem('user_info', JSON.stringify(userData))

      addSentryBreadcrumb('User info updated', 'auth')
      return { success: true }
    } catch (err) {
      const error = err as Error
      setError(error.message)
      
      captureSentryException(error, {
        store: 'AuthStore',
        action: 'updateUserInfo'
      })
      
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const isTokenExpired = () => {
    if (!tokens.value?.access_token) {
      return true
    }
    
    return authService.isTokenExpired(tokens.value.access_token)
  }

  const getValidAccessToken = async (): Promise<string | null> => {
    try {
      if (!tokens.value?.access_token) {
        return null
      }

      // If token is expired, try to refresh
      if (isTokenExpired()) {
        const result = await refreshTokens()
        if (!result.success) {
          return null
        }
      }

      return tokens.value?.access_token || null
    } catch (err) {
      captureSentryException(err as Error, {
        store: 'AuthStore',
        action: 'getValidAccessToken'
      })
      return null
    }
  }

  return {
    // State
    isAuthenticated,
    user,
    tokens,
    loading,
    error,
    
    // Getters
    isLoggedIn,
    accessToken,
    idToken,
    refreshToken,
    userEmail,
    userName,
    
    // Actions
    login,
    logout,
    handleAuthCallback,
    refreshTokens,
    checkAuthState,
    updateUserInfo,
    clearAuthentication,
    isTokenExpired,
    getValidAccessToken,
    setLoading,
    setError
  }
})